/**
 * Firmflow Admin Portal – design tokens + MUI theme
 */
import { createTheme, alpha } from '@mui/material/styles';

export const tokens = {
  color: {
    bg: '#F7F8FA',
    surface: '#FFFFFF',
    subtle: '#FBFBFD',
    ink: '#0B1220',
    ink2: '#434A57',
    ink3: '#8A93A6',
    ink4: '#C8CEDA',
    border: '#E6E9EF',
    borderHv: '#D3D8E2',
    brand: '#3E4CE8',
    brandHv: '#2F3BCB',
    brandTint: '#EEF0FE',
    success: '#137A4A',
    successBg: '#E6F4EC',
    warning: '#B15E00',
    warningBg: '#FFF3E0',
    danger: '#B42318',
    dangerBg: '#FDECEC',
    info: '#0A5FC2',
    infoBg: '#E6F0FB',
    disabled: '#F1F3F7',
    /** Narrow rail (Sidebar) — aligns with ink */
    sidebarBg: '#0B1220',
  },
  radius: { sm: 6, md: 10, lg: 12, xl: 16 },
  shadow: {
    sm: '0 1px 2px rgba(16,24,40,.06)',
    md: '0 4px 12px rgba(16,24,40,.08)',
    lg: '0 12px 32px rgba(16,24,40,.12)',
  },
  space: (n) => n * 4,

  // Layout (used by PageLayout, GlobalSearch, CommandPalette, EventsCalendar)
  pageSpacing: 3,
  headerSpacing: 4,
  sidebarWidth: 80,
  topBarHeight: 56,
  contentMaxWidth: 1400,
  searchBarMaxWidth: 400,
  commandPaletteWidth: 600,
  commandPaletteMinWidth: 560,
  commandPaletteMaxWidth: 640,
  eventsCalendarVerticalOffsetPx: 140,
};

const { color: c, radius: r } = tokens;

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: c.brand,
      dark: c.brandHv,
      light: alpha(c.brand, 0.14),
      contrastText: '#fff',
    },
    secondary: {
      main: c.ink2,
      contrastText: '#fff',
    },
    success: { main: c.success, light: c.successBg },
    warning: { main: c.warning, light: c.warningBg },
    error: { main: c.danger, light: c.dangerBg },
    info: { main: c.info, light: c.infoBg },
    background: {
      default: c.bg,
      paper: c.surface,
    },
    text: {
      primary: c.ink,
      secondary: c.ink2,
      disabled: c.ink4,
    },
    divider: c.border,
    action: {
      active: alpha(c.ink, 0.56),
      hover: alpha(c.ink, 0.06),
      selected: alpha(c.brand, 0.1),
      disabled: alpha(c.ink, 0.28),
      disabledBackground: c.disabled,
    },
  },
  shape: {
    borderRadius: r.md,
  },
  typography: {
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.02em',
      color: c.ink,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      color: c.ink,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      color: c.ink,
    },
    body1: { fontSize: 15, lineHeight: 1.55, color: c.ink },
    body2: { fontSize: 14, lineHeight: 1.5, color: c.ink2 },
    caption: { fontSize: 13, color: c.ink3 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: c.bg,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: r.md,
        },
        sizeSmall: {
          fontSize: '0.8125rem',
          padding: '4px 12px',
        },
        sizeMedium: {
          fontSize: '0.875rem',
          padding: '7px 18px',
        },
        sizeLarge: {
          fontSize: '0.9375rem',
          padding: '10px 24px',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        /* Do not add border/shadow to all rounded papers — that made the whole app look “boxed” / unclean. */
        rounded: {
          borderRadius: r.lg,
        },
        elevation1: {
          boxShadow: tokens.shadow.md,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: r.lg,
          boxShadow: tokens.shadow.lg,
          border: `1px solid ${c.border}`,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: r.md,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: r.sm,
        },
      },
    },
  },
});
